import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    makeStyles,
    createStyles,
    useMediaQuery
} from '@material-ui/core';

import {
    GridCol,
    Typography,
    Variant,
    Police,
    Button,
    ButtonVariant,
    ContainerMui,
    Spacer
} from '../../styles';
import {
    IResumeOffre,
    hasSoumissionAssurance,
    isVariableProtege,
    isVariableReduit,
    isRevisableFerme
} from '../../hooks';
import {
    PretActuelModal,
    DetailsOffreModalChoixCoEmprunteur,
    TexteAvecInfoBulle
} from '..';
import { NoteTauxInteret } from './noteTauxInteret/NoteTauxInteret';

const useStyles = makeStyles(() =>
    createStyles({
        interest: {
            marginRight: '0.5rem'
        },
        titreCaracteristiquePret: {
            fontSize: '24px !important'
        }
    })
);

export interface IResumePretProps {
    resumeOffre: IResumeOffre;
    isEditButtonDisplayed?: boolean;
    isSoldeSansAssuranceDisplayed: boolean;
    showButtonPretActualModal?: boolean;
    onModifiedClick: () => void;
    onTooltipActive: () => void;
}

export const ResumePret: React.FC<IResumePretProps> = ({
    resumeOffre,
    isEditButtonDisplayed,
    showButtonPretActualModal,
    isSoldeSansAssuranceDisplayed,
    onModifiedClick,
    onTooltipActive
}) => {
    const [t] = useTranslation(['ResumePret']);
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:640px)');

    const { titreOffre, assurance, offreChoisie } = resumeOffre;
    const { codeAssurance } = assurance;
    const {
        valeurTauxPropose,
        valeurTauxEffectif,
        valeurTauxPlafond,
        tauxRabaisSubsequent,
        montantRemboursementSansAssurance
    } = offreChoisie;

    const isSoumissionAssurance = hasSoumissionAssurance(codeAssurance);
    const isOfferVariableProtege = isVariableProtege(offreChoisie);
    const isOfferVariableReduit = isVariableReduit(offreChoisie);
    const isOffreRevisableFerme = isRevisableFerme(offreChoisie);
    const shoudTooltipBeDisplayed =
        isOfferVariableReduit ||
        isOfferVariableProtege ||
        isOffreRevisableFerme;

    const renderInterestRate = () => {
        const interestRateValue =
            isOfferVariableProtege && valeurTauxEffectif
                ? valeurTauxEffectif
                : valeurTauxPropose;

        return t('Number:percent', {
            value: interestRateValue
        });
    };

    const getLibelleInfobulle = () => {
        if (isOfferVariableReduit) {
            return t('resumePretInterestLibelleInfobulleOffreVariableReduit');
        } else if (isOfferVariableProtege) {
            return (
                t('resumePretInterestLibelleInfobulleOffreVariableProtege1') +
                t('Number:percent', {
                    value: valeurTauxPlafond
                }) +
                t('resumePretInterestLibelleInfobulleOffreVariableProtege2')
            );
        } else if (isOffreRevisableFerme) {
            return (
                t('resumePretInterestLibelleInfobulleOffreRevisable1') +
                t('Number:percent', {
                    value: tauxRabaisSubsequent
                }) +
                t('resumePretInterestLibelleInfobulleOffreRevisable2')
            );
        } else return 'erreur libelle infobulle non trouvé';
    };

    return (
        <ContainerMui
            padding={isMobile ? '1rem' : '1.5rem'}
            dataTestId="bloc_taux_validation"
            spacerDistance={6}
            spacerDistanceMobile={5}
        >
            <Typography
                variant={Variant.h3}
                police={Police.bold}
                className={classes.titreCaracteristiquePret}
                marginBottom="1rem"
            >
                {t('resumePretTitle')}
            </Typography>
            <Typography variant={Variant.p} marginBottom="0.25rem">
                {t('resumePretTypeTitle')}
            </Typography>
            <Typography
                variant={Variant.p}
                police={Police.bold}
                marginBottom="1rem"
            >
                {titreOffre}
            </Typography>
            {shoudTooltipBeDisplayed ? (
                <>
                    <TexteAvecInfoBulle
                        label={t('resumePretInterestOffreVariableOuRevisable')}
                        labelInfoBulle={getLibelleInfobulle()}
                        labelHorsEcranInfoBulle={t(
                            'resumePretInterestLibelleInfobulleHorsEcranOffreVariableReduitProtegeRevisable'
                        )}
                        isSmallerLabelInfobulle={isMobile}
                        onToolTipActive={onTooltipActive}
                    />
                    <Spacer all={2} />
                </>
            ) : (
                <Typography variant={Variant.p} marginBottom="0.25rem">
                    {t('resumePretInterestTitle')}
                </Typography>
            )}
            <Box display="flex" alignItems="center">
                <Typography
                    variant={Variant.p}
                    police={Police.bold}
                    hideMarginBottom={!isEditButtonDisplayed}
                >
                    {renderInterestRate()}
                </Typography>
                <NoteTauxInteret
                    isSimplifie={false}
                    resumeOffre={resumeOffre}
                    hideMarginBottom={!isEditButtonDisplayed}
                />
            </Box>
            {isSoumissionAssurance && isSoldeSansAssuranceDisplayed && (
                <>
                    <Typography variant={Variant.p} marginBottom="0.25rem">
                        {t('resumePretInsuranceTitlePaiementSansAssurance')}
                    </Typography>
                    <Typography
                        variant={Variant.p}
                        police={Police.bold}
                        marginBottom="1rem"
                        hideMarginBottom={!isEditButtonDisplayed}
                    >
                        {t('Number:currency', {
                            value: montantRemboursementSansAssurance
                        })}
                    </Typography>
                </>
            )}
            {isEditButtonDisplayed && (
                <Button
                    id={'QA_test_validation_date_modifier'}
                    variant={ButtonVariant.compact}
                    dataCy="qa-button-date-modifier"
                    dataTestId={'bouton_modifier_offre'}
                    onClick={onModifiedClick}
                    srOnlySuffix={t('resumeChangeButtonHorsParcours')}
                >
                    {t('resumeChangeButton')}
                </Button>
            )}
            {showButtonPretActualModal && (
                <Box mb={isMobile ? -2 : -1} mt={-2.5} ml={isMobile ? -2 : 0}>
                    <GridCol col="12">
                        <Box mb={2}>
                            <DetailsOffreModalChoixCoEmprunteur
                                resumeOffre={resumeOffre}
                            />
                        </Box>
                        <PretActuelModal />
                    </GridCol>
                </Box>
            )}
        </ContainerMui>
    );
};
